import { mutationOptions } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';

import type {
  RebookAppointmentParams,
  RebookAppointmentResponse,
} from './types';

/**
 * Creates a new cart from a past appointment for rebooking.
 *
 * This endpoint lets the backend handle service ID resolution,
 * ensuring the cart is created with valid service IDs that match
 * the barber's current assignedServices.
 *
 * @param params - The appointment ID to rebook from
 * @returns The created cart with pre-populated services
 */
const rebookAppointment = async ({
  appointmentId,
}: RebookAppointmentParams): Promise<RebookAppointmentResponse> => {
  const { data } = await apiClient.post<RebookAppointmentResponse>(
    `/v1/booking/${appointmentId}/rebook`,
  );

  return data;
};

export const rebookAppointmentMutation = (
  options?: UseMutationOptions<
    RebookAppointmentResponse,
    Error,
    RebookAppointmentParams
  >,
) =>
  mutationOptions({
    mutationFn: (params: RebookAppointmentParams) => rebookAppointment(params),
    retry: false,
    retryDelay: (attemptIndex: number = 0) =>
      Math.min(1000 * 2 ** attemptIndex, 2000),
    ...options,
  });
